import {ArtworkLink,PlatformLink} from '../lib/toolAccess.jsx'
import { useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { IconArrow, IconCheck } from '../components/icons.jsx'
import { BUILDERS, BUILDER_BY_ID, ENGINE_STATUS } from '../lib/builders.js'
import { flowBlockedReason, flowSupported } from '../features/artwork/studio/flows.js'
import styles from './ProductBuilders.module.css'

export default function ProductBuilders() {
  const [openId, setOpenId] = useState(BUILDERS[0].id)
  const active = BUILDER_BY_ID[openId]

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Configurators"
        title="Product Builders"
        lead="Guided builders for rings, jackets, trophies, and awards — each grounded in the same verified school brand kits as Artwork Studio and priced live through the Pricing Engine."
        actions={
          <span className={styles.badge}>
            <span className={styles.badgeDot} />
            Builder shells ready
          </span>
        }
      />

      <div className={styles.layout}>
        {/* Builder catalog */}
        <div className={styles.grid}>
          {BUILDERS.map((b) => {
            const st = ENGINE_STATUS[b.engine]
            const isOpen = b.id === openId
            return (
              <button
                key={b.id}
                type="button"
                className={`card ${styles.tile} ${isOpen ? styles.tileActive : ''}`}
                onClick={() => setOpenId(b.id)}
                aria-pressed={isOpen}
              >
                <div className={styles.tileTop}>
                  <span className={styles.tileName}>{b.name}</span>
                  <span className={styles.status} data-tone={st.tone}>
                    {st.label}
                  </span>
                </div>
                <span className={styles.tileTag}>{b.tagline}</span>
              </button>
            )
          })}
        </div>

        {/* Detail panel */}
        <aside className={`card ${styles.detail}`}>
          <div className={styles.detailHead}>
            <div>
              <div className="eyebrow">Builder</div>
              <h2 className={`${styles.detailTitle} display`}>{active.name}</h2>
            </div>
            <span className={styles.status} data-tone={ENGINE_STATUS[active.engine].tone}>
              {ENGINE_STATUS[active.engine].label}
            </span>
          </div>

          <p className={styles.blurb}>{active.blurb}</p>

          <div className={styles.block}>
            <div className="eyebrow">What you configure</div>
            <ul className={styles.inputs}>
              {active.inputs.map((i) => (
                <li key={i}>
                  <IconCheck width={15} height={15} />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.block}>
            <div className="eyebrow">Compose — live hand-offs</div>
            <div className={styles.composes}>
              {active.composes.includes('pricing') && (
                <PlatformLink to={`/pricing?product=${active.priceProduct}`} className={styles.composeLink}>
                  Price it · Pricing Engine
                  <IconArrow width={14} height={14} />
                </PlatformLink>
              )}
              {active.composes.includes('artwork') && active.artworkFlow && flowSupported(active.artworkFlow) && (
                <ArtworkLink to={`/artwork-studio?flow=${active.artworkFlow}`} className={styles.composeLink}>
                  Generate concept · Artwork Studio
                  <IconArrow width={14} height={14} />
                </ArtworkLink>
              )}
              {/* NOT A HIDDEN BUTTON — A STATED GAP. A flow with no Builder
                  destination used to render the same confident link as one that
                  works, and land the rep on the Builder with the flow silently
                  discarded. Saying which piece is missing is the difference
                  between "not built yet" and "broken". */}
              {active.composes.includes('artwork') && active.artworkFlow && !flowSupported(active.artworkFlow) && (
                <p className={styles.composeNote}>{flowBlockedReason(active.artworkFlow)}</p>
              )}
            </div>
          </div>

          <div className={styles.launchRow}>
            <button
              type="button"
              className="btn btn-primary"
              disabled
              title="The interactive 3D builder runtime is not bundled in this repo yet"
            >
              Launch 3D builder
            </button>
            <span className={styles.launchNote}>
              3D runtime not bundled — but pricing &amp; concept hand-offs above are live.
            </span>
          </div>
        </aside>
      </div>

      {/* Honest status: only the 3D runtime is the gap now */}
      <section className={`card ${styles.needs}`}>
        <div className="eyebrow">What’s live vs. what’s next</div>
        <div className={styles.needsGrid}>
          <div className={styles.need}>
            <div className={styles.needTitle}>✓ Brand kit + pricing + concept (live)</div>
            <p>
              Each builder hands off to the real modules: verified school colors
              from Artwork Studio, live quotes from the Pricing Engine (via{' '}
              <code>priceProduct</code>), and an AI concept proof (via{' '}
              <code>artworkFlow</code>) — the compose links above.
            </p>
          </div>
          <div className={styles.need}>
            <div className={styles.needTitle}>A 3D configurator runtime</div>
            <p>
              The interactive all-in-one 3D builder needs a WebGL/three.js viewer
              (or a hosted configurator) — not yet bundled here. It mounts into
              the framed slot once chosen.
            </p>
          </div>
          <div className={styles.need}>
            <div className={styles.needTitle}>Owned product assets</div>
            <p>
              Base meshes, textures, and component art must be hosted in the
              system of record (no free-floating links) and exposed through the
              backend for the 3D builder to load.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
